/*
 * ZUCK cupom-carrinho (wdpgrxhyra)
 * Cart-drawer coupon field for Versiani (Dawn).
 * Applies / removes a discount code via the Ajax Cart API (Shopify Plus:
 * POST /cart/update.js { discount }) and re-renders the drawer footer + items
 * from the same response's `sections` payload — single fetch, single render,
 * no double-render, no publish (the free-ship progress bar tracks subtotal and
 * is outside the swapped regions, so it is never touched by the coupon).
 */
if (!customElements.get('cart-coupon-estimator')) {
  class CartCouponEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.panel = this.querySelector('[data-coupon-panel]');
      this.summary = this.querySelector('[data-coupon-summary]');
      this.floatHost = null;
      this.keyboardInsetsBound = false;
      this.keyboardWatchId = null;
      this.savedItemsScrollTop = 0;
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleActionClick = this.handleActionClick.bind(this);
      this.handleCouponFocus = this.handleCouponFocus.bind(this);
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleActionClick);
      document.removeEventListener('focusin', this.handleCouponFocus);
      this.stopKeyboardWatch();
      this.teardownKeyboardInsets();
    }

    get updateUrl() {
      return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    }

    bindEvents() {
      document.addEventListener('click', this.handleActionClick);
      document.addEventListener('focusin', this.handleCouponFocus);

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('#cart-drawer-coupon')) {
          event.preventDefault();
          this.applyCoupon();
        }
      });
    }

    isEventInScope(event) {
      return this.contains(event.target) || this.floatHost?.contains(event.target);
    }

    handleCouponFocus(event) {
      if (!event.target.matches('#cart-drawer-coupon')) return;
      if (!this.isEventInScope(event)) return;
      this.startKeyboardWatch();
    }

    handleActionClick(event) {
      if (!this.isEventInScope(event)) return;

      const target = event.target.closest('button');
      if (!target) return;

      if (target.matches('[data-open-coupon]')) {
        event.preventDefault();
        this.openCoupon();
      } else if (target.matches('[data-remove-coupon]')) {
        event.preventDefault();
        this.removeCoupon();
      } else if (target.matches('[data-apply-coupon]')) {
        event.preventDefault();
        this.applyCoupon();
      } else if (target.matches('[data-estimator-back]')) {
        event.preventDefault();
        this.closePanel();
      }
    }

    openCoupon() {
      const activeCode =
        this.querySelector('[data-coupon-code]')?.textContent?.trim() ||
        this.querySelector('[data-open-coupon].cart-coupon__code')?.textContent?.trim() ||
        '';
      this.panel.innerHTML = `
        <div class="cart-coupon__form">
          <div class="cart-coupon__field-row">
            <input id="cart-drawer-coupon" type="text" name="discount" value="${this.escapeHtml(activeCode)}"
              placeholder="Cupom de desconto" aria-label="Código do cupom" autocomplete="off"
              autocapitalize="characters" autocorrect="off" spellcheck="false" data-panel-autofocus>
            <button type="button" class="cart-coupon__apply" data-apply-coupon>Aplicar</button>
            <button type="button" class="cart-coupon__close" aria-label="Fechar" data-estimator-back>&times;</button>
          </div>
          <p class="cart-coupon__message" data-coupon-message hidden></p>
        </div>
      `;
      this.panel.hidden = false;
      this.classList.add('is-panel-open');

      const input = this.panel.querySelector('#cart-drawer-coupon');
      if (input) {
        try {
          input.focus({ preventScroll: true });
        } catch (error) {
          input.focus();
        }
        const len = input.value.length;
        try {
          input.setSelectionRange(len, len);
        } catch (error) {}
      }

      requestAnimationFrame(() => {
        this.setupKeyboardInsets();
        this.startKeyboardWatch();
      });
    }

    closePanel() {
      this.stopKeyboardWatch();
      this.teardownKeyboardInsets();
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this.classList.remove('is-panel-open');
      this.unlockDrawerScroll();
    }

    getCouponInput() {
      return this.floatHost?.querySelector('#cart-drawer-coupon') || this.panel?.querySelector('#cart-drawer-coupon');
    }

    getCouponForm() {
      return this.floatHost?.querySelector('.cart-coupon__form') || this.panel?.querySelector('.cart-coupon__form');
    }

    lockDrawerScroll() {
      const items = document.querySelector('cart-drawer-items');
      this.savedItemsScrollTop = items ? items.scrollTop : 0;
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.add('coupon-input-open');
    }

    unlockDrawerScroll() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('coupon-input-open');
    }

    mountFloatingForm(form) {
      if (!form || this.floatHost) return;

      this.floatHost = document.createElement('div');
      this.floatHost.className = 'cart-coupon-float-host';
      this.floatHost.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.floatHost);
      this.floatHost.appendChild(form);
    }

    returnFormToPanel() {
      if (!this.floatHost || !this.panel) return;

      const form = this.floatHost.querySelector('.cart-coupon__form');
      if (form) {
        this.clearCouponAnchorOn(form);
        this.panel.appendChild(form);
      }

      this.floatHost.remove();
      this.floatHost = null;
    }

    setupKeyboardInsets() {
      if (this.keyboardInsetsBound) return;
      this.keyboardInsetsBound = true;
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleViewportChange);
        window.visualViewport.addEventListener('scroll', this.handleViewportChange);
      }
      window.addEventListener('resize', this.handleViewportChange);
    }

    teardownKeyboardInsets() {
      if (!this.keyboardInsetsBound) return;
      this.keyboardInsetsBound = false;
      this.returnFormToPanel();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleViewportChange);
        window.visualViewport.removeEventListener('scroll', this.handleViewportChange);
      }
      window.removeEventListener('resize', this.handleViewportChange);
    }

    startKeyboardWatch() {
      this.stopKeyboardWatch();
      let ticks = 0;
      this.handleViewportChange();
      this.keyboardWatchId = window.setInterval(() => {
        this.handleViewportChange();
        ticks += 1;
        if (ticks >= 16) this.stopKeyboardWatch();
      }, 50);
    }

    stopKeyboardWatch() {
      if (!this.keyboardWatchId) return;
      window.clearInterval(this.keyboardWatchId);
      this.keyboardWatchId = null;
    }

    getKeyboardInset() {
      if (!window.visualViewport) return 0;
      const viewport = window.visualViewport;
      return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    }

    shouldFloatForm() {
      if (!this.classList.contains('is-panel-open')) return false;
      return this.getKeyboardInset() >= 20;
    }

    clearCouponAnchorOn(form) {
      if (!form) return;
      form.classList.remove('is-keyboard-anchored');
      form.style.left = '';
      form.style.width = '';
      form.style.bottom = '';
      form.style.top = '';
    }

    restoreDrawerScroll() {
      const items = document.querySelector('cart-drawer-items');
      if (items) items.scrollTop = this.savedItemsScrollTop;
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    }

    focusCouponInput() {
      const input = this.getCouponInput();
      if (!input || document.activeElement === input) return;
      try {
        input.focus({ preventScroll: true });
      } catch (error) {
        input.focus();
      }
    }

    handleViewportChange() {
      if (!this.classList.contains('is-panel-open')) return;

      let form = this.getCouponForm();
      if (!form) return;

      this.restoreDrawerScroll();

      const viewport = window.visualViewport;
      const shouldFloat = this.shouldFloatForm();

      if (!shouldFloat) {
        if (this.floatHost) {
          this.returnFormToPanel();
          this.unlockDrawerScroll();
          this.focusCouponInput();
        }
        return;
      }

      if (!this.floatHost) {
        const panelForm = this.panel.querySelector('.cart-coupon__form') || form;
        this.mountFloatingForm(panelForm);
        this.lockDrawerScroll();
        form = this.getCouponForm();
        this.focusCouponInput();
      }

      if (!form || !viewport) return;

      const drawerInner = document.querySelector('cart-drawer .drawer__inner');
      const drawerRect = drawerInner
        ? drawerInner.getBoundingClientRect()
        : { left: viewport.offsetLeft + 12, width: viewport.width - 24 };

      const horizontalPadding = 12;
      const left = Math.max(drawerRect.left + horizontalPadding, viewport.offsetLeft + horizontalPadding);
      const maxWidth = Math.min(drawerRect.width - horizontalPadding * 2, viewport.width - horizontalPadding * 2);
      const visibleBottom = viewport.offsetTop + viewport.height;
      const formHeight = form.offsetHeight || 56;

      form.classList.add('is-keyboard-anchored');
      form.style.left = `${left}px`;
      form.style.width = `${Math.max(0, maxWidth)}px`;
      form.style.top = `${Math.max(viewport.offsetTop + 8, visibleBottom - formHeight - 8)}px`;
      form.style.bottom = 'auto';
    }

    async applyCoupon() {
      const input = this.getCouponInput();
      const code = (input?.value || '').trim();
      const message = this.getCouponForm()?.querySelector('[data-coupon-message]');
      if (!code) {
        this.setMessage(message, 'Digite um cupom para aplicar.');
        return;
      }

      this.setLoading(true);
      this.setMessage(message, '');

      try {
        const state = await this.updateDiscount(code);
        const entry = (state.discount_codes || []).find(
          (d) => (d.code || '').toLowerCase() === code.toLowerCase()
        );
        if (!entry || entry.applicable === false) {
          await this.updateDiscount('');
          this.setMessage(message, 'Não foi possível aplicar este cupom. Confira o código e tente novamente.');
          return;
        }
        this.closePanel();
        await this.rerender(state);
      } catch (error) {
        this.setMessage(message, 'Não foi possível aplicar este cupom. Confira o código e tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    async removeCoupon() {
      if (!this.querySelector('[data-remove-coupon]')) return;

      this.setLoading(true);
      try {
        const state = await this.updateDiscount('');
        const stillHasCode = (state.discount_codes || []).some((entry) => entry.applicable !== false);
        if (stillHasCode) {
          throw new Error('Discount code still applied');
        }
        this.closePanel();
        await this.rerender(state);
      } catch (error) {
        const message = this.querySelector('[data-coupon-message]') || this.createTransientMessage();
        this.setMessage(message, 'Não foi possível remover o cupom. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    createTransientMessage() {
      let message = this.querySelector('[data-coupon-summary] .cart-coupon__message');
      if (message) return message;
      message = document.createElement('p');
      message.className = 'cart-coupon__message';
      message.setAttribute('data-coupon-message', '');
      this.querySelector('[data-coupon-summary]')?.appendChild(message);
      return message;
    }

    async updateDiscount(discount) {
      const response = await fetch(this.updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('Discount update failed');
      return response.json();
    }

    async rerender(state) {
      const sectionHtml = state?.sections?.['cart-drawer'];
      if (sectionHtml) {
        this.replaceDrawerFragments(sectionHtml);
        return;
      }

      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      try {
        const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
        if (!response.ok) throw new Error('Section fetch failed');
        const html = await response.text();
        this.replaceDrawerFragments(html);
      } catch (error) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-coupon-estimator', cartData: state });
        }
      }
    }

    replaceDrawerFragments(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const target = document.querySelector(selector);
        const source = doc.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
    }

    setMessage(node, text) {
      if (!node) return;
      node.textContent = text || '';
      node.hidden = !text;
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      const applyBtn = this.getCouponForm()?.querySelector('[data-apply-coupon]');
      if (applyBtn) applyBtn.disabled = isLoading;
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }
  }

  customElements.define('cart-coupon-estimator', CartCouponEstimator);
}
